import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, SEARCH_KEY } from "@/lib/api";
import { AuthShell, destinationAfterAuth } from "@/pages/Login";

export function AuthCallback() {
  const loc = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sessionId = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    const role = localStorage.getItem("cz_google_role");
    const search = localStorage.getItem(SEARCH_KEY);
    api.post("/auth/google/session", { session_id: sessionId, role, search_session_id: role === "consumer" ? search : null })
      .then(({ data }) => {
        window.history.replaceState(null, "", window.location.pathname);
        setUser(data.user);
        nav(destinationAfterAuth(data.user, search, null), { replace: true });
      })
      .catch((e) => { toast.error(errMsg(e)); setUser(false); nav("/login", { replace: true }); });
  }, [loc.hash, nav, setUser]);
  return <Loader label="Signing you in" />;
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const { user, refresh } = useAuth();
  const [state, setState] = useState(token ? "verifying" : "idle");
  const [devLink, setDevLink] = useState(sessionStorage.getItem("cz_dev_verify"));
  const ran = useRef(false);
  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    api.post("/auth/verify-email", { token }).then(() => { setState("done"); sessionStorage.removeItem("cz_dev_verify"); refresh(); }).catch(() => setState("error"));
  }, [token, refresh]);
  const resend = async () => {
    try { const { data } = await api.post("/auth/resend-verification"); setDevLink(data.dev_verification_link); toast.success("Verification email sent"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <AuthShell>
      <span className="h-12 w-12 rounded-2xl bg-teal-50 text-teal-700 grid place-items-center"><MailCheck className="h-6 w-6" /></span>
      <h1 className="text-2xl font-bold mt-4" data-testid="verify-email-title">{state === "done" ? "Email verified" : state === "error" ? "Link invalid or expired" : state === "verifying" ? "Verifying…" : "Verify your email"}</h1>
      {state === "done" && <Button asChild className="mt-6 rounded-full bg-[#003B2D]"><Link to="/">Continue</Link></Button>}
      {(state === "idle" || state === "error") && user && !user.email_verified && (
        <div className="mt-4 space-y-4">
          <p className="text-slate-600">We sent a verification link to {user.email}.</p>
          <Button variant="outline" className="rounded-full" onClick={resend} data-testid="resend-verification-btn">Resend verification email</Button>
          {devLink && <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="dev-verify-link"><b>Development mode (email delivery MOCKED):</b> <a className="underline text-teal-800 break-all" href={devLink}>open verification link</a></div>}
        </div>
      )}
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <AuthShell>
      <h1 className="text-2xl font-bold">Reset your password</h1>
      {sent ? <p className="text-slate-600 mt-3" data-testid="forgot-sent-msg">If an account exists for {email}, a reset link has been sent.</p> : (
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="forgot-email-input" /></div>
          <Button type="submit" className="w-full h-11 rounded-full bg-[#003B2D]" data-testid="forgot-submit-btn">Send reset link</Button>
        </form>
      )}
      <Link to="/login" className="block text-sm text-teal-700 font-semibold mt-6">Back to sign in</Link>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); toast.success("Password updated. Please sign in."); nav("/login"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <AuthShell>
      <h1 className="text-2xl font-bold">Choose a new password</h1>
      <form onSubmit={submit} className="space-y-4 mt-6">
        <div className="space-y-1.5"><Label>New password</Label><Input type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" data-testid="reset-password-input" /></div>
        <Button type="submit" className="w-full h-11 rounded-full bg-[#003B2D]" data-testid="reset-submit-btn">Update password</Button>
      </form>
    </AuthShell>
  );
}
